import React from 'react';
import { AlertTriangle, X, ArchiveRestore, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useRecoveryLogic } from './hooks/useRecoveryLogic';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';

const RecoveryFileCardContent: React.FC = () => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = React.useState(false);
  const {
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    recentRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
  } = useRecoveryLogic();

  const handleRecoverClick = () => {
    if (firstRecord) {
      handleRecover(firstRecord);
    }
  };

  const handleDismissClick = (fileId: string) => {
    handleDismiss(fileId);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{
        opacity: { duration: 0.15, ease: 'easeOut' },
        scale: { duration: 0.15, ease: 'easeOut' },
      }}
      role="button"
      tabIndex={0}
      onClick={handleRecoverClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRecoverClick();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative flex flex-col h-full border border-border rounded-lg overflow-hidden bg-white dark:bg-background cursor-pointer"
    >
      {/* Thumbnail Area */}
      <div className="relative overflow-hidden">
        <div className="w-full aspect-video bg-muted flex items-center justify-center">
          <div className="flex flex-col items-center gap-1">
            <div className="relative w-6 h-6">
              <ArchiveRestore
                className={`absolute w-6 h-6 text-muted-foreground transition-all duration-300 ease-out ${
                  isHovered ? 'opacity-100 scale-105' : 'opacity-0 scale-95'
                }`}
              />
              <AlertTriangle
                className={`absolute w-6 h-6 text-muted-foreground transition-all duration-300 ease-out ${
                  isHovered ? 'opacity-0 scale-95' : 'opacity-60'
                }`}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {isHovered
                ? t('library.recovery.status.finalize')
                : t('library.recovery.status.interrupted')}
            </span>
          </div>
        </div>

        {/* Dismiss Button - matches FileCard action menu placement */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7 w-7 p-0 rounded-md"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (firstRecord) {
                      handleDismissClick(firstRecord.fileId);
                    }
                  }}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('library.recovery.dismiss')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Progress indicator - matches FileCard duration badge style */}
        {isRecovering && (
          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 text-xs font-medium rounded backdrop-blur-sm bg-black/70 text-white flex items-center gap-1.5">
            <Loader className="w-3 h-3 animate-spin" />
            {Math.round(progress)}%
          </div>
        )}
      </div>

      {/* Content - matches FileCard exactly */}
      <div className="p-3 relative flex-grow flex flex-col">
        <div className="block text-inherit no-underline flex-grow">
          {/* Title - matches FileCard */}
          <h3 className="text-sm font-medium line-clamp-2 leading-snug mb-1.5 min-h-[2.5rem]">
            {hasMultiple
              ? t('library.recovery.title.multiple', {
                  count: recentRecoverableFiles.length,
                })
              : t('library.recovery.title.single')}
          </h3>

          {/* Date - matches FileCard */}
          <p className="text-xs text-muted-foreground">
            {firstRecord && getTimeAgo(firstRecord.startedAt)}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Main component that includes Grid wrapper
const RecoveryFileCard: React.FC = () => {
  const { hasRecoverableFiles } = useRecoveryLogic();

  // Don't render if no recent recoverable files
  if (!hasRecoverableFiles) {
    return null;
  }

  return (
    <div className="col-span-1">
      <RecoveryFileCardContent />
    </div>
  );
};

export default RecoveryFileCard;
